import { Component, Input, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { PageParams } from 'src/app/core/models/page-params.model';
import { IProduct } from 'src/app/modules/consolidate-shipments/models/order-details.model';
import { ISaleOrder } from 'src/app/modules/sales-order-management/models/sale-order.interface';
import { DeliveryStatusLabelPipe } from 'src/app/shared/pipes/delivery-status-label.pipe';
import { deliveredStatuses } from 'src/app/core/constants.enum';
import {
    formatPickupDaysRemaining,
    getDaysInWarehouse,
    getPickupCountdownStatusClass,
    getSaleOrderOperationalStatusId,
    hasPickupMissingStatusAlert,
} from 'src/app/shared/helpers/vintedgo-pickup-countdown.util';
import { getConsolidatedStatusLabel } from 'src/app/shared/helpers/consolidate-shipment-status-groups.util';

export type WarehouseReviewMode = 'products' | 'saleOrders';

@Component({
    selector: 'eci-warehouse-review-dialog',
    templateUrl: './warehouse-review-dialog.component.html',
    styleUrls: ['./warehouse-review-dialog.component.scss']
})
export class WarehouseReviewDialogComponent implements OnInit {
    @Input() title = '';
    @Input() mode: WarehouseReviewMode = 'products';
    @Input() products: IProduct[] = [];
    @Input() saleOrders: ISaleOrder[] = [];

    productColumns: string[] = [
        'numberOfUnits',
        'productName',
        'productCategory',
        'sku',
        'price',
        'hscode'
    ];
    saleOrderColumns: string[] = [
        'parcelCount',
        'orderDate',
        'shopName',
        'marketplaceName',
        'marketplaceOrderId',
        'destination',
        'totalOrderVat',
        'carrier',
        'trackingNumber',
        'parcelWeight',
        'parcelHeight',
        'parcelWidth',
        'parcelDepth',
        'daysInWarehouse',
        'pickupCountdown',
        'aftershipStatus',
    ];

    productDataSource: MatTableDataSource<IProduct>;
    saleOrderDataSource: MatTableDataSource<ISaleOrder>;
    pageParams: PageParams = new PageParams();
    showFooter = true;
    private readonly deliveryStatusLabelPipe = new DeliveryStatusLabelPipe();

    constructor(
        public dialogRef: MatDialogRef<WarehouseReviewDialogComponent>,
    ) {
    }

    ngOnInit(): void {
        this.productDataSource = new MatTableDataSource<IProduct>(this.products || []);
        this.saleOrderDataSource = new MatTableDataSource<ISaleOrder>(this.saleOrders || []);
        this.pageParams.length = (this.products || []).length;
    }

    close(): void {
        this.dialogRef.close();
    }

    getOrderOperationalStatus(order: ISaleOrder): number | null {
        return getSaleOrderOperationalStatusId(order);
    }

    getIsSoDelivered(status): boolean {
        return deliveredStatuses.includes(Number(status));
    }

    getPickupDaysLabel(order: ISaleOrder): string {
        return formatPickupDaysRemaining(this.getOrderOperationalStatus(order), order.readyForPickupDate);
    }

    getDaysInWarehouseLabel(order: ISaleOrder): string {
        const days = getDaysInWarehouse(order);
        return days == null ? '-' : String(days);
    }

    getPickupStatusClass(order: ISaleOrder): string {
        return getPickupCountdownStatusClass(this.getOrderOperationalStatus(order), order.readyForPickupDate);
    }

    isPickupMissingStatus(order: ISaleOrder): boolean {
        return hasPickupMissingStatusAlert({
            status: this.getOrderOperationalStatus(order),
            readyForPickupDate: order.readyForPickupDate,
        });
    }

    getOrderStatusDisplayLabel(order: ISaleOrder): string {
        const status = this.getOrderOperationalStatus(order);
        if (status == null) {
            return '';
        }
        const groupedLabel = getConsolidatedStatusLabel(status);
        if (groupedLabel) {
            return groupedLabel;
        }
        return this.deliveryStatusLabelPipe.transform(status);
    }
}
